/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * PageBuilderEditorPage — main 2-pane page builder editor.
 * Left: SectionPalette | Centre: BuilderCanvas | Right (slide): PropertiesDrawer
 * Route: /dashboard/applications/:id/page-builder/:pageId/edit
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { usePageBuilder } from '../hooks/usePageBuilder';
import { usePage, saveSections, publishPage, savePageTemplate } from '../hooks/usePages';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../theme/ThemeContext';

import SectionPalette from '../components/page-builder/SectionPalette';
import BuilderCanvas from '../components/page-builder/BuilderCanvas';
import PropertiesDrawer from '../components/page-builder/PropertiesDrawer';
import BuilderToolbar from '../components/page-builder/BuilderToolbar';
import LivePreview from '../components/page-builder/LivePreview';
import SeoEditorModal from '../components/page-builder/SeoEditorModal';
import HeaderFooterEditor from '../components/page-builder/HeaderFooterEditor';
import HtmlCssWorkspaceModal from '../components/page-builder/HtmlCssWorkspaceModal';
import { useAlertDialog } from '../components/common/AlertDialogContext';

const AUTOSAVE_DELAY = 3000; // 3 s debounce

export default function PageBuilderEditorPage() {
  const { id: appId, pageId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  // ── Load page from API ──────────────────────────────────────────────────────
  const queryClient = useQueryClient();
  const { data: pageData, isLoading, isError } = usePage(pageId);

  // ── Builder state ───────────────────────────────────────────────────────────
  const {
    sections,
    isDirty,
    canUndo,
    canRedo,
    dispatch,
    markSaved,
    setSections,
  } = usePageBuilder([]);

  const [pageStatus, setPageStatus] = useState('draft');
  const [pageVisibility, setPageVisibility] = useState('public');
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [htmlWorkspaceClientId, setHtmlWorkspaceClientId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showSeo, setShowSeo] = useState(false);
  const [pageSeo, setPageSeo] = useState({});
  const [showHeaderFooter, setShowHeaderFooter] = useState(false);
  const [pageConfig, setPageConfig] = useState({});
  const [isSaving, setIsSaving]         = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [toast, setToast]               = useState(null); // { message, type: 'success'|'error' }
  const { showAlertDialog } = useAlertDialog();

  // ── Seed builder from page data ──────────────────────────────────────────────
  // Track the last updated_at we seeded from so that when React Query returns
  // fresh data (newer updated_at) we re-seed even if we already seeded from a
  // stale cache entry. A simple boolean ref would block re-seeding after the
  // stale cache hit — which is the root cause of blank canvases on re-entry.
  const seededAtRef = useRef(null);
  const isDirtyRef = useRef(isDirty);
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);

  useEffect(() => {
    if (!pageData) return;
    if (isDirtyRef.current) return; // never overwrite unsaved changes
    if (seededAtRef.current === pageData.updated_at) return; // same version, skip
    seededAtRef.current = pageData.updated_at;
    setSections(pageData.sections || []);
    setPageStatus(pageData.status || 'draft');
    setPageVisibility(pageData.visibility || 'public');
    setPageSeo(pageData.seo || {});
    setPageConfig(pageData.page_config || {});
  }, [pageData, setSections]);

  // ── Derived: selected section object ────────────────────────────────────────
  const selectedSection = selectedClientId
    ? sections.find(s => s._clientId === selectedClientId) || null
    : null;

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handleSelectSection = useCallback((clientId) => {
    const sec = sections.find(s => s._clientId === clientId);
    if (sec?.section_type === 'html_css_workspace') {
      setSelectedClientId(null); // keep drawer closed
      setHtmlWorkspaceClientId(clientId);
    } else {
      setHtmlWorkspaceClientId(null);
      setSelectedClientId(prev => (prev === clientId ? null : clientId));
    }
  }, [sections]);

  const handleCloseDrawer = useCallback(() => setSelectedClientId(null), []);

  const handleAddSection = useCallback((type) => {
    dispatch({ type: 'ADD_SECTION', sectionType: type });
  }, [dispatch]);

  const handleRemoveSection = useCallback((clientId) => {
    const sec = sections.find(s => s._clientId === clientId);
    const label = sec ? sec.section_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'this section';
    showAlertDialog({
      title: 'Delete Section',
      message: `Delete "${label}"? This cannot be undone.`,
      type: 'warning',
      okLabel: 'Delete',
      cancelLabel: 'Cancel',
      showCancel: true,
      onOk: () => {
        dispatch({ type: 'REMOVE_SECTION', clientId });
        setSelectedClientId(prev => (prev === clientId ? null : prev));
      },
    });
  }, [dispatch, sections, showAlertDialog]);

  // ── Save draft ──────────────────────────────────────────────────────────────
  const doSave = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const result = await saveSections(pageId, sections);
      // Feed the DB-assigned IDs back into the builder so subsequent saves
      // update existing rows instead of creating duplicates.
      if (Array.isArray(result)) {
        // Restore frontend _clientId values so the selected drawer stays open.
        // The backend doesn't know about _clientId — without this, all sections
        // get new UUIDs after save, which makes selectedSection resolve to null
        // and silently closes the properties drawer.
        const clientIdByDbId = {};
        sections.forEach(s => { if (s.id) clientIdByDbId[s.id] = s._clientId; });
        const merged = result.map(s => ({
          ...s,
          _clientId: (s.id && clientIdByDbId[s.id]) || s._clientId,
        }));
        setSections(merged);
      } else {
        markSaved();
      }
      showToast('Draft saved.');
    } catch (err) {
      showToast(err.message || 'Save failed.', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [pageId, sections, markSaved, setSections, showToast, isSaving]);

  // ── Auto-save debounce ──────────────────────────────────────────────────────
  const autoSaveTimer = useRef(null);
  useEffect(() => {
    if (!isDirty || !seededAtRef.current) return;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      doSave();
    }, AUTOSAVE_DELAY);
    return () => clearTimeout(autoSaveTimer.current);
  }, [isDirty, sections, doSave]);

  // ── Load page template onto canvas ─────────────────────────────────────────
  const handleAddPageTemplate = useCallback((tpl) => {
    const tplSections = tpl.default_config?.sections || [];
    if (tplSections.length === 0) return;
    const doLoad = () => {
      setSections(tplSections.map((s, i) => ({ ...s, order: i })));
      showToast(`Page template "${tpl.name}" loaded.`);
    };
    if (sections.length > 0) {
      showAlertDialog({
        title: 'Load Page Template',
        message: `Loading "${tpl.name}" will replace the ${sections.length} section(s) currently on the canvas. This cannot be undone.`,
        type: 'warning',
        okLabel: 'Load Template',
        cancelLabel: 'Keep Current',
        showCancel: true,
        onOk: doLoad,
      });
    } else {
      doLoad();
    }
  }, [sections, setSections, showToast, showAlertDialog]);

  // ── Save page as template ───────────────────────────────────────────────────
  const handleSavePageTemplate = useCallback(async (name) => {
    await savePageTemplate(appId, name, sections);
    queryClient.invalidateQueries({ queryKey: ['sectionTemplates', appId] });
    showToast('Page template saved.');
  }, [appId, sections, queryClient, showToast]);

  // ── Publish ─────────────────────────────────────────────────────────────────
  const handlePublish = useCallback(async () => {
    setIsPublishing(true);
    try {
      const savedResult = await saveSections(pageId, sections);
      if (Array.isArray(savedResult)) {
        setSections(savedResult);
      } else {
        markSaved();
      }
      const result = await publishPage(pageId);
      setPageStatus(result.status || 'published');
      showToast('Page published successfully!');
    } catch (err) {
      showToast(err.message || 'Publish failed.', 'error');
    } finally {
      setIsPublishing(false);
    }
  }, [pageId, sections, markSaved, setSections, showToast]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        dispatch({ type: 'UNDO' });
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        dispatch({ type: 'REDO' });
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        doSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dispatch, doSave]);

  // ── Loading / Error states ──────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '100vh' }}>
        <span className="spinner-border" style={{ color: theme.primary }} />
      </div>
    );
  }

  if (isError || !pageData) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: '100vh', gap: 12 }}>
        <i className="bi bi-exclamation-triangle" style={{ fontSize: 40, color: '#dc2626' }} />
        <div className="text-muted">Failed to load page.</div>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>

      {/* Toolbar */}
      <BuilderToolbar
        pageName={pageData.name}
        pageStatus={pageStatus}
        isDirty={isDirty}
        canUndo={canUndo}
        canRedo={canRedo}
        isSaving={isSaving}
        isPublishing={isPublishing}
        onUndo={() => dispatch({ type: 'UNDO' })}
        onRedo={() => dispatch({ type: 'REDO' })}
        onSaveDraft={doSave}
        onPublish={handlePublish}
        onPreview={() => setShowPreview(true)}
        onSeo={() => setShowSeo(true)}
        onHeaderFooter={() => setShowHeaderFooter(true)}
        onSavePageTemplate={handleSavePageTemplate}
        onBack={() => navigate(`/dashboard/applications/${appId}/page-builder`)}
      />

      {/* 2-Pane body */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Left: Palette */}
        <div
          style={{
            width: 180,
            flexShrink: 0,
            borderRight: '1px solid #e2e8f0',
            background: '#f8fafc',
            overflowY: 'auto',
          }}
        >
          <SectionPalette
            appId={appId}
            onAdd={handleAddSection}
            onAddTemplate={(tpl) =>
              dispatch({
                type: 'ADD_SECTION',
                sectionType: tpl.section_type,
                configOverride: tpl.default_config || {},
              })
            }
            onAddPageTemplate={handleAddPageTemplate}
          />
        </div>

        {/* Centre: Canvas */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#f1f5f9', padding: '20px 24px' }}>
          <BuilderCanvas
            sections={sections}
            dispatch={dispatch}
            selectedClientId={selectedClientId}
            onSelectSection={handleSelectSection}
            onAddSection={handleAddSection}
            onRemoveSection={handleRemoveSection}
            appId={appId}
            pageId={pageId}
          />
        </div>
      </div>

      {/* Right: Properties Drawer (fixed, slide from right) */}
      <PropertiesDrawer
        section={selectedSection}
        dispatch={dispatch}
        appId={appId}
        allSections={sections}
        pageVisibility={pageVisibility}
        onClose={handleCloseDrawer}
        onSave={doSave}
        isSaving={isSaving}
      />

      {/* HTML/CSS/JS Workspace — full-screen modal editor */}
      {htmlWorkspaceClientId && (() => {
        const hwSection = sections.find(s => s._clientId === htmlWorkspaceClientId);
        return hwSection ? (
          <HtmlCssWorkspaceModal
            section={hwSection}
            dispatch={dispatch}
            appId={appId}
            page={{
              id: pageData.id,
              slug: pageData.slug,
              name: pageData.name,
              visibility: pageVisibility,
              seo: pageSeo,
              page_config: pageConfig,
            }}
            sectionCount={sections.length}
            onClose={() => setHtmlWorkspaceClientId(null)}
          />
        ) : null;
      })()}

      {/* Header & Footer editor modal */}
      {showHeaderFooter && (
        <HeaderFooterEditor
          pageId={pageId}
          appId={appId}
          allSections={sections}
          initialPageConfig={pageConfig}
          onClose={() => setShowHeaderFooter(false)}
          onSaved={(newConfig) => setPageConfig(newConfig)}
        />
      )}

      {/* SEO editor modal */}
      {showSeo && (
        <SeoEditorModal
          pageId={pageId}
          initialSeo={pageSeo}
          onClose={() => setShowSeo(false)}
          onSaved={(newSeo) => setPageSeo(newSeo)}
        />
      )}

      {/* Live Preview modal */}
      {showPreview && (
        <LivePreview
          sections={sections}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Toast notification */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: toast.type === 'error' ? '#dc2626' : '#16a34a',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            zIndex: 9999,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <i className={`bi ${toast.type === 'error' ? 'bi-x-circle' : 'bi-check-circle'}`} />
          {toast.message}
        </div>
      )}
    </div>
  );
}
